import { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Spinner,
  TextButton,
} from '@zillow/constellation-10';
import { IconHouseFilled } from '@zillow/constellation-icons';

import { useClientAppConfigWebHandler_getFeature } from '@zg-rentals/rental-manager-env';
import { GTM_EVENTS } from '@zg-rentals/google-tag-manager';

import { listingErrors as listingErrorsReducer, listing as listingReducer } from '../../../reducers/listing';

import { RMWContext } from '../../../providers/RMWContext';
import { updateIsWizardView } from '../../../slices/app';

import { SingleFamilyEnterpriseSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import useContracts from '../../../hooks/useContracts';
import useValidateListing from '../../../hooks/useValidateListing';

import { useGetListingDetails } from '../../../hooks/queries/properties';

import PostingActions from '../../../actions/PostingActions';

import UserActions from '../../../actions/UserActions';

import PropertiesActions from '../../../actions/PropertiesActions';

import accessUtils from '../../../utils/accessUtils';
import contextUtils from '../../../utils/contextUtils';
import propertyUtils from '../../../utils/propertyUtils';
import stringHelper from '../../../utils/stringHelper';
import typeCheckUtils from '../../../utils/typeCheckUtils';
import { trackGtmEvent } from '../../../../client/analytics';

import AddPropertyConstants from '../../../constants/AddPropertyConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import TrackingEventsConstants from '../../../constants/TrackingEventsConstants';

import AddPropertyModalCSEvents from '../../../model/ClickstreamEvent/AddPropertyModalCSEvents';
import MultiplexCSEvents from '../../../model/ClickstreamEvent/MultiplexCSEvents';

import AddPropertyForm from './components/AddPropertyForm';
import ConfirmAddress from './components/ConfirmAddress';
import DeleteButton from './components/DeleteButton';
import MultiplexUnitEntry from '../../MultiplexUnitEntry';
import MultiplexUnitsAdded from '../../MultiplexUnitsAdded';
import { shouldDisplayRoomForRentCheckbox } from './utils';
import SelectAvailableActions from './components/SelectAvailableActions';

import type { ErrorRMWProps } from '../../../model/ErrorRMW';
import type { ListingReducerProps } from '../../../reducers/listing';
import type { MultiplexUnitEntryRef } from '../../MultiplexUnitEntry';
import type { MultiplexUnitsAddedRef } from '../../MultiplexUnitsAdded';
import type { UserPropertyMultiplexCreateUnitResponseWebDto } from '@zg-rentals/rental-manager-env';

const {
  LISTING_CODES_BY_TYPE,
  PROPERTY_TYPES,
  PROPERTY_TYPES: { COMMUNITY, MULTIPLEX },
  LISTING_TYPES: { RENTAL, ROOM },
  PROPERTY_CODES_BY_TYPE,
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;
const {
  EMPTY_ERROR,
  EMPTY_LISTING,
  MAX_NUM_MULTIPLEX_UNITS_ERROR,
  MAX_NUM_MULTIPLEX_UNITS_VALUE,
  MAX_NUM_UNITS_ERROR,
  MAX_NUM_UNITS_VALUE,
  NUMBER_OF_UNITS_ERROR,
  POSITIVE_NUMBER_OF_UNITS_ERROR,
} = AddPropertyConstants;

const { getUrlQueryParams } = stringHelper;

const STEPS = {
  CONFIRM_ADDRESS: 'CONFIRM_ADDRESS',
  CREATE_LISTING: 'CREATE_LISTING',
  INPUT_ADDRESS: 'INPUT_ADDRESS',
  SELECT_ACTION: 'SELECT_ACTION',
  UNITS_ADDED: 'UNITS_ADDED',
  UNIT_ENTRY: 'UNIT_ENTRY',
} as const;

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  behavior?: (typeof AddPropertyConstants.BEHAVIOR_OPTIONS)[keyof typeof AddPropertyConstants.BEHAVIOR_OPTIONS];
};

const AddPropertyModal = ({
  isOpen = false,
  onClose = () => {},
  behavior = AddPropertyConstants.BEHAVIOR_OPTIONS.ADD,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);
  const location = useLocation();
  const multiplexUnitEntryRef = useRef<MultiplexUnitEntryRef>(null);
  const multiplexUnitsAddedRef = useRef<MultiplexUnitsAddedRef>(null);

  const { search } = location;

  const shouldRedirectToPayments = contextUtils.isRedirectToPayments(search);

  const queryClient = useQueryClient();

  const contracts = useSelector((state) => SingleFamilyEnterpriseSelectors.makeContracts(state));
  const { currentProperty, user, isWizardView, appConfig, cookieEnabledServices } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
      isWizardView: state.app.isWizardView,
      appConfig: state.app.appConfig,
      cookieEnabledServices: state.app.cookieEnabledServices,
    }),
    shallowEqual,
  );

  const [step, setStep] = useState<(typeof STEPS)[keyof typeof STEPS]>(STEPS.INPUT_ADDRESS);
  const [isSelectActionFinalStep, setIsSelectActionFinalStep] = useState(false);
  const [unitNumberDetected, setUnitNumberDetected] = useState(false);
  const [isValidatedWithoutUnitDetected, setIsValidatedWithoutUnitDetected] = useState(false);
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<JSX.Element | string>
  >('');
  const [isRefocusingAddressInput, setIsRefocusingAddressInput] = useState(false);
  const [unitsCreated, setUnitsCreated] = useState<Array<UserPropertyMultiplexCreateUnitResponseWebDto>>([]);
  const [hasPartialUnitsSucceeded, setHasPartialUnitsSucceeded] = useState<boolean>(false);

  const { isFetching: isGetListingDetailsFetching, data: propertyDetailsData } = useGetListingDetails(
    currentProperty.listingAlias,
    {
      enabled: currentProperty.listingAlias && behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT,
    },
  );

  const getInitialListing = () => {
    const urlQueryParams: Record<string, unknown> = getUrlQueryParams();
    const urlContainsAddressParam = urlQueryParams?.address !== undefined;

    return !urlContainsAddressParam
      ? EMPTY_LISTING
      : {
          ...EMPTY_LISTING,
          address: (urlQueryParams?.address as string).replace(/-/g, ' '),
          unit: urlQueryParams.unit as string,
        };
  };

  const initialListing = getInitialListing();

  const [listing, setListing] = useReducer(listingReducer, initialListing);
  const [errorObj, setErrorObj] = useReducer(listingErrorsReducer, EMPTY_ERROR);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const [isImpreciseLocation, setIsImpreciseLocation] = useState(false);

  const { isPending: isPendingCanAccessMultiplex, data: canAccessMultiplexResponse = 'false' } =
    useClientAppConfigWebHandler_getFeature({
      appName: 'zrm_web',
      featureName: 'canAccessMultiplex',
    });

  const canAccessNoWelcomePageML = accessUtils.canAccessNoWelcomePageML({ appConfig });

  const canAccessMultiplex = accessUtils.canAccessMultiplex({
    canAccessMultiplexResponse,
    cookieEnabledServices,
  }) as boolean;

  const isMultiplexType = listing?.propertyType === MULTIPLEX;

  // bypass unit number check for multiplex type
  const isPromptUnitNumberCheckRequired = isMultiplexType ? false : !unitNumberDetected;

  const { verifyCanSubmitForm, handleCheckLocationRestriction } = useValidateListing(
    listing,
    setListing,
    errorObj,
    setErrorObj,
    setUnitNumberDetected,
    setIsValidatedWithoutUnitDetected,
    setPropertyAlreadyPostedWarningMessage,
    setPropertyId,
    setIsImpreciseLocation,
  );
  useContracts(setListing, setErrorObj, setIsSubmitDisabled);

  useEffect(() => {
    if (isOpen) {
      dispatch(
        UserActions.trackEvent({
          trackingEvent: TrackingEventsConstants.OPEN_ADD_PROPERTY_MODAL,
        }),
      );
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (
      !isGetListingDetailsFetching &&
      !isPendingCanAccessMultiplex &&
      propertyDetailsData &&
      behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT
    ) {
      setListing({
        type: AddPropertyConstants.UPDATE_ALL,
        payload: {
          address: propertyUtils.formatAddress(currentProperty).formattedAddressWithoutUnit,
          isRoomForRent: String(currentProperty.listingTypeCode === Number(LISTING_CODES_BY_TYPE[ROOM])),
          numUnits: currentProperty.numUnits,
          customerId: currentProperty.customerId,
          propertyType: currentProperty.propertyType,
          unit: currentProperty.unit,
          isAddressEditable: currentProperty.isAddressEditable,
          duplicateAccountListingAlias: null,
          listingAlias: currentProperty.listingAlias,
        },
      });

      if (!currentProperty.isAddressValid) {
        setErrorObj({ type: 'address', payload: 'Please provide a valid address' });
      }
    }
  }, [behavior, isPendingCanAccessMultiplex, propertyDetailsData, isOpen]);

  const handleListingChange = (keyName: keyof ListingReducerProps, newValue: string) => {
    const newListing = {
      ...listing,
      [keyName]: newValue,
    };

    setErrorObj({ type: keyName, payload: '' });
    // if the unit was changed, the entire address will be revalidated
    keyName === 'unit' && setErrorObj({ type: 'address', payload: '' });

    const isMultiplexType = newListing.propertyType === MULTIPLEX;

    if (newListing.numUnits) {
      const numUnitsValue = newListing.numUnits;
      const isNumericValue = typeCheckUtils.isNumeric(numUnitsValue as string | number | undefined);
      const hasDecimal = String(numUnitsValue).includes('.');

      if (!isNumericValue || hasDecimal) {
        setErrorObj({ type: 'numUnits', payload: NUMBER_OF_UNITS_ERROR });
      } else {
        const numUnits = Number(numUnitsValue);

        if (numUnits <= 0) {
          setErrorObj({ type: 'numUnits', payload: POSITIVE_NUMBER_OF_UNITS_ERROR });
        } else if (isMultiplexType && numUnits > MAX_NUM_MULTIPLEX_UNITS_VALUE) {
          // multiplex
          setErrorObj({ type: 'numUnits', payload: MAX_NUM_MULTIPLEX_UNITS_ERROR });
        } else if (!isMultiplexType && numUnits > MAX_NUM_UNITS_VALUE) {
          // community / other numUnits types
          setErrorObj({ type: 'numUnits', payload: MAX_NUM_UNITS_ERROR });
        }
      }
    }

    setListing({ type: keyName, payload: keyName === 'numUnits' ? newValue?.trim() : newValue });
  };

  const onChangePropertyType = (newPropertyType: string) => handleListingChange('propertyType', newPropertyType);

  const verifyInputAndGoToConfirmSteps = (csEvent: () => void, onValidationSuccess?: () => void) => {
    setIsSubmitDisabled(true);

    verifyCanSubmitForm({ isPromptUnitNumberCheckRequired })
      .then((isValid) => {
        if (isValid) {
          // run any cleanup logic before changing step
          onValidationSuccess?.();
          setStep(STEPS.CONFIRM_ADDRESS);
          csEvent();
        }
      })
      .finally(() => {
        setIsSubmitDisabled(false);
        setIsPrimaryButtonLoading(false);
        setIsSecondaryButtonLoading(false);
      });
  };

  const createProperty = async () => {
    const { isRoomForRent, propertyType, address, unit, numUnits, customerId, duplicateAccountListingAlias } = listing;
    const listingType = isRoomForRent === 'true' ? ROOM : RENTAL;
    const isCommunityType = propertyType === COMMUNITY;

    // Duplicate listing exists. Redirect to existing listing rather than creating new one
    if (duplicateAccountListingAlias) {
      setListing({
        type: 'listingAlias',
        payload: duplicateAccountListingAlias,
      });

      const path = isSelectActionFinalStep ? null : `${paths.propertiesPage}/${duplicateAccountListingAlias}/listing`;
      return { path };
    }

    const responseData = await dispatch(
      PostingActions.property.create({
        listingType,
        propertyType,
        fullAddress: address,
        ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
        customerId: listingType === ROOM ? null : customerId,
      }),
    );

    // @ts-expect-error
    const { listingAlias } = responseData;

    // feature access
    const canAccessModularListing = accessUtils.canAccessModularListing({
      cookieEnabledServices,
      // @ts-expect-error
      currentProperty: responseData,
      user,
    });

    // analytics
    const clickstreamEvent =
      canAccessModularListing && isSelectActionFinalStep
        ? AddPropertyModalCSEvents.ConfirmSeeAllActions
        : AddPropertyModalCSEvents.ConfirmCreateListing;

    // @ts-expect-error - legacy api does not include types
    clickstreamEvent({ currentProperty: responseData, user });
    trackGtmEvent({ event: GTM_EVENTS.RENTAL_MANAGER_ADD_PROPERTY });

    setListing({
      type: 'listingAlias',
      payload: listingAlias,
    });

    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

    // these early returns matter.
    if (isSelectActionFinalStep) {
      // if the user hit "See All Available Actions" earlier,
      // they go to the SELECT_ACTIONS step in the wizard.
      // they DO NOT transition to a new URL.
      return { path: null };
    }
    if (canAccessModularListing) {
      if (!isWizardView) dispatch(updateIsWizardView(true));
      return { path: `${paths.propertiesPage}/${listingAlias}/create-listing`, additionalData: responseData };
    }
    return {
      path: isSelectActionFinalStep ? null : `${paths.propertiesPage}/${listingAlias}/listing`,
      additionalData: responseData,
    };
  };

  const handleErrorReset = () => {
    setIsSubmitDisabled(false);

    // take user back to add property form to see the error.
    setStep(STEPS.INPUT_ADDRESS);
    setIsPrimaryButtonLoading(false);
    setIsSecondaryButtonLoading(false);
  };

  const editProperty = () => {
    dispatch(
      PropertiesActions.savePropertyInfo({
        fullAddress: listing.address,
        ...(!currentProperty.isMultiFamily && { unit: listing.unit }),
        numUnits: listing.numUnits,
        propertyId: listing.listingAlias,
        // @ts-expect-error
        propertyTypeCode:
          PROPERTY_CODES_BY_TYPE[listing.propertyType as (typeof PROPERTY_TYPES)[keyof typeof PROPERTY_TYPES]],
      }),
    )
      // @ts-expect-error
      .then(() => {
        window.location.reload();
        queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
      })
      .catch((err: ErrorRMWProps) => {
        if (err.message) {
          setErrorObj({ type: 'address', payload: err.message });
          handleErrorReset();
        }
      });
  };

  const handleMultiplexUnityEntryCreateStart = () => {
    setIsPrimaryButtonLoading(true);
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntrySuccess = ({
    unitsCreated,
    hasPartialUnitsSucceeded,
  }: {
    unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
    hasPartialUnitsSucceeded: boolean;
  }) => {
    setUnitsCreated(unitsCreated);
    setHasPartialUnitsSucceeded(hasPartialUnitsSucceeded);
    setStep(STEPS.UNITS_ADDED);
    setIsPrimaryButtonLoading(false);
    // disable button until a unit is selected
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntryErrorChange = ({
    hasError,
    message,
  }: {
    hasError?: boolean | undefined;
    message?: string | undefined;
  }) => {
    if (hasError) {
      setIsPrimaryButtonLoading(false);
      setIsSubmitDisabled(true);
    } else {
      setIsPrimaryButtonLoading(false);
      setIsSubmitDisabled(false);
    }
  };

  const handleActiveUnitIndexChange = (activeUnitIndex: number | null) => {
    setIsSubmitDisabled(activeUnitIndex === null);
  };

  const handlePrimaryButtonClick = async () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS: {
        const isCommunityType = listing?.propertyType === COMMUNITY;

        const isRoomForRentCheckboxDisplayed = shouldDisplayRoomForRentCheckbox(
          isMultiplexType,
          isCommunityType,
          behavior,
        );

        setIsPrimaryButtonLoading(true);
        setIsSelectActionFinalStep(false);
        verifyInputAndGoToConfirmSteps(
          () => AddPropertyModalCSEvents.CreateListing({ user }),
          // onValidationSuccess
          () => {
            // set isRoomForRent to false if checkbox was not displayed
            if (isRoomForRentCheckboxDisplayed === false) {
              setListing({
                type: 'isRoomForRent',
                payload: 'false',
              });
            }
          },
        );

        if (isValidatedWithoutUnitDetected) {
          setIsValidatedWithoutUnitDetected(false);
        }

        if (unitNumberDetected && listing.unit) {
          setUnitNumberDetected(false);
        }

        break;
      }
      case STEPS.CONFIRM_ADDRESS: {
        setIsPrimaryButtonLoading(true);
        setIsSubmitDisabled(true);
        if (behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.ADD) {
          try {
            // check if multiplex type
            if (isMultiplexType) {
              // don't create the listing, go to unit entry step
              MultiplexCSEvents.MultiplexConfirmAddressClick({ user });

              setStep(STEPS.UNIT_ENTRY);
              setIsPrimaryButtonLoading(false);
              setIsSubmitDisabled(false);
              return;
            }

            const { path, additionalData } = await createProperty();
            if (isSelectActionFinalStep) {
              setStep(STEPS.SELECT_ACTION);
            } else if (path) {
              if (shouldRedirectToPayments) {
                // @ts-expect-error
                window.router.updateLocation(`${paths.paymentsDashboardPage}/${additionalData.listingAlias}`);
              } else {
                window.router.transitionTo(path, additionalData);
              }
              onClose();
            }
          } catch (err) {
            const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
            const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';

            setErrorObj({ type: fieldWithError, payload: err.message });
            handleErrorReset();
            return { path: null };
          }
        } else {
          editProperty();
        }

        break;
      }
      case STEPS.UNIT_ENTRY: {
        MultiplexCSEvents.MultiplexConfirmUnitEntryClick({ user });
        multiplexUnitEntryRef.current?.createMultiplex?.();
        break;
      }
      case STEPS.UNITS_ADDED: {
        const activeUnitListingAlias = multiplexUnitsAddedRef.current?.getActiveUnitListingAlias?.();

        const currentPropertyWithSelectedListingAlias = {
          ...currentProperty,
          listingAlias: activeUnitListingAlias,
        };

        MultiplexCSEvents.MultiplexAddListingDetailsClick({
          currentProperty: currentPropertyWithSelectedListingAlias,
          user,
        });
        multiplexUnitsAddedRef.current?.goToSelectedUnitListingCreation?.();
        break;
      }
      default: {
        throw Error('unrecognized step: ' + step);
      }
    }
  };

  const handleSecondaryButtonClick = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        setIsSecondaryButtonLoading(true);
        setIsSelectActionFinalStep(true);
        verifyInputAndGoToConfirmSteps(() => AddPropertyModalCSEvents.SeeAllActions({ user }));
        break;
      case STEPS.CONFIRM_ADDRESS:
        AddPropertyModalCSEvents.GoBackAndEditAddress({ user });
        setStep(STEPS.INPUT_ADDRESS);
        setIsRefocusingAddressInput(true);
        break;
      default:
        throw Error('unrecognized step: ' + step);
    }
  };

  const primaryButtonDataEventId = useMemo(() => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return AddPropertyModalCSEvents.CreateListing.data_event_id;

      case STEPS.UNIT_ENTRY:
        return MultiplexCSEvents.MultiplexConfirmUnitEntryClick.data_event_id;

      case STEPS.UNITS_ADDED:
        return MultiplexCSEvents.MultiplexAddListingDetailsClick.data_event_id;

      default:
        if (isSelectActionFinalStep) {
          return AddPropertyModalCSEvents.ConfirmSeeAllActions.data_event_id;
        }

        return AddPropertyModalCSEvents.ConfirmCreateListing.data_event_id;
    }
  }, [step, isSelectActionFinalStep]);

  const getPrimaryButtonTitle = () => {
    if (behavior === AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT) {
      return 'Save changes';
    } else {
      const nonMultiplexButtonTitle = shouldRedirectToPayments ? 'Start collecting rent' : 'Confirm';

      const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : nonMultiplexButtonTitle;

      switch (step) {
        case STEPS.INPUT_ADDRESS:
          return 'Create listing';
        case STEPS.CONFIRM_ADDRESS:
          return confirmAddressButtonTitle;
        case STEPS.UNIT_ENTRY:
          return 'Save and continue';
        case STEPS.UNITS_ADDED:
          return 'Add listing details';
        default:
          return '';
      }
    }
  };

  const getSecondaryButtonTitle = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return 'See all available actions';
      case STEPS.CONFIRM_ADDRESS:
        return 'Edit property information';
      default:
        return '';
    }
  };

  const getSubheadingText = () => {
    if (shouldRedirectToPayments) {
      return 'Once you add your property, you can start collecting rent payments for free.';
    } else {
      return canAccessNoWelcomePageML
        ? 'Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your perfect renter.'
        : 'This basic information sets up the property in Zillow Rental Manager. Then you can create a rental listing for it.';
    }
  };

  return (
    <Modal
      scrollDocument={false}
      useDismissProps={{ outsidePress: currentProperty.isAddressValid, escapeKey: currentProperty.isAddressValid }}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      header={
        <Heading level={1} textStyle="heading-xs">
          Zillow Rental Manager
        </Heading>
      }
      body={
        <div aria-live="polite">
          {step === STEPS.INPUT_ADDRESS && (
            <Box
              css={{
                paddingBlock: { lg: '800' },
                paddingLeft: { lg: '800' },
                paddingRight: { lg: '20px' },
              }}
            >
              <Icon
                size="xl"
                css={{ color: 'icon.accent.blue.hero', marginTop: { base: '600', md: 0 } }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ marginBlock: { base: '400', md: '600' } }}>
                First, let&rsquo;s add your property
              </Heading>

              <Paragraph
                css={{
                  paddingBottom: { mdDown: '200' },
                  marginBottom: {
                    md:
                      unitNumberDetected ||
                      isValidatedWithoutUnitDetected ||
                      Boolean(propertyAlreadyPostedWarningMessage)
                        ? '600'
                        : '800',
                  },
                }}
              >
                {getSubheadingText()}
              </Paragraph>

              <AddPropertyForm
                listing={listing}
                canAccessMultiplex={canAccessMultiplex}
                contracts={contracts}
                errorObj={errorObj}
                isSubmitDisabled={isSubmitDisabled}
                setIsSubmitDisabled={setIsSubmitDisabled}
                behavior={behavior}
                unitNumberDetected={unitNumberDetected}
                isValidatedWithoutUnitDetected={isValidatedWithoutUnitDetected}
                propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
                onListingChange={handleListingChange}
                onCheckLocationRestriction={(keyName, newValue) =>
                  handleCheckLocationRestriction(onChangePropertyType, keyName, newValue)
                }
                isRefocusingAddressInput={isRefocusingAddressInput}
                setIsRefocusingAddressInput={setIsRefocusingAddressInput}
              />
            </Box>
          )}
          {step === STEPS.CONFIRM_ADDRESS && (
            <Box css={{ padding: { lg: '800' } }} data-testid="AddPropertyModal-ConfirmAddress">
              <Icon
                size="xl"
                css={{
                  color: 'icon.accent.blue.hero',
                  marginTop: { base: '600', md: 0 },
                  marginBottom: '600',
                }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ paddingBottom: { base: '600', lg: '800' } }}>
                Is this information complete and correct?
              </Heading>

              <ConfirmAddress
                address={listing.address as string}
                canAccessMultiplex={canAccessMultiplex}
                isRoomForRent={
                  typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true'
                }
                numUnits={listing.numUnits as string | undefined}
                propertyType={
                  canAccessMultiplex && listing.propertyType === PROPERTY_TYPES.CONDO
                    ? 'Condo'
                    : (listing.propertyType as string)
                }
                unit={listing.unit as string | undefined}
                showImpreciseLocationAlert={isImpreciseLocation}
              />
            </Box>
          )}
          {step === STEPS.SELECT_ACTION && (
            <SelectAvailableActions listingAlias={listing.listingAlias as string} triggerObject="posting_path" />
          )}
          {user.features.canDeleteListing &&
            user.isPostingPathAdmin &&
            !currentProperty.isFeed &&
            currentProperty.listingAlias && (
              <Box css={{ paddingInline: { base: '0', lg: '800' } }}>
                <DeleteButton />
              </Box>
            )}
          {step === STEPS.UNIT_ENTRY && (
            <MultiplexUnitEntry
              ref={multiplexUnitEntryRef}
              customerId={listing.customerId}
              fullAddress={listing.address as string}
              numUnits={listing.numUnits as string | undefined}
              onSuccess={handleMultiplexUnityEntrySuccess}
              onErrorChange={handleMultiplexUnityEntryErrorChange}
              onCreateStart={handleMultiplexUnityEntryCreateStart}
            />
          )}
          {step === STEPS.UNITS_ADDED && (
            <MultiplexUnitsAdded
              ref={multiplexUnitsAddedRef}
              hasPartialUnitsSucceeded={hasPartialUnitsSucceeded}
              unitsCreated={unitsCreated}
              onActiveUnitIndexChange={handleActiveUnitIndexChange}
            />
          )}
        </div>
      }
      footer={
        step !== STEPS.SELECT_ACTION && (
          <ButtonGroup aria-label="Action Buttons">
            {!shouldRedirectToPayments && (
              <TextButton
                tone="brand"
                onClick={handleSecondaryButtonClick}
                disabled={isSubmitDisabled}
                data-eventid={
                  step === STEPS.INPUT_ADDRESS
                    ? AddPropertyModalCSEvents.SeeAllActions.data_event_id
                    : AddPropertyModalCSEvents.GoBackAndEditAddress.data_event_id
                }
              >
                {isSecondaryButtonLoading ? (
                  <Spinner size="sm" css={{ marginRight: '10px' }} />
                ) : (
                  getSecondaryButtonTitle()
                )}
              </TextButton>
            )}
            <Button
              data-testid="primary-button"
              data-eventid={primaryButtonDataEventId}
              fluid={false}
              tone="brand"
              emphasis="filled"
              onClick={handlePrimaryButtonClick}
              disabled={isSubmitDisabled}
              loading={isPrimaryButtonLoading}
            >
              {getPrimaryButtonTitle()}
            </Button>
          </ButtonGroup>
        )
      }
      data-eventid={AddPropertyModalCSEvents.Open.data_event_id}
    />
  );
};

export default AddPropertyModal;
